import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { DfModuleImageModule } from '@df/module-image/df-module-image.module';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { HideUpComponent } from '@df/ui/hide-up/hide-up.component';
import { ToggleModule } from '@df/ui/toggle/toggle.module';
import { ToggleService } from '@df/ui/toggle/toggle.service';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { IfSizeDirective } from '../../../../../../git_modules/@tomandco/dragonfly-ui/src/common/if-size.directive';
import { BottomPanelAreaDirective } from '../../common/bottom-panel/bottom-panel-area.directive';
import { iconsOptions } from '../../styles/cms.config';

export namespace NPageFooter {
    export const ID = 'page-footer';
    export interface IData {
        logo: string;
        menus: IMenu[];
        bottomImage?: string;
        bottomLinks: ILink[];
        paymentMethods: IPaymentMethod[];
    }

    interface IMenu {
        type: 'link' | 'description';
        heading: string;
        items: IMenuItem[];
    }

    interface IMenuItem {
        title: string;
        description: string;
        url?: string;
        icon?: string;
    }

    interface ILink {
        text: string;
        url: string;
    }

    interface IPaymentMethod {
        name: string;
        icon: string;
    }
}

@Component({
    standalone: true,
    imports: [
        DfModuleImageModule,
        RouterLink,
        ICmsClientModule,
        ToggleModule,
        HideUpComponent,
        IfSizeDirective,
        CommonModule,
        BottomPanelAreaDirective,
        HrefComponent
    ],
    selector: 'page-footer',
    templateUrl: 'page-footer.html',
    styleUrl: 'page-footer.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
@ICmsComponent<PageFooterComponent>({
    id: NPageFooter.ID,
    name: 'Page Footer',
    description: 'Page footer block',
    block: {
        url: NPageFooter.ID,
        scope: 'website',
        autoload: 'prerender'
    },
    onDemandOnly: true,
    fields: [
        <NICmsContentField.IField>{
            type: NICmsContentField.EFieldType.image,
            key: 'logo',
            title: 'Logo',
            required: true
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'menus',
            title: 'Menus',
            itemName: 'menu',
            itemNameField: 'heading',
            maxSize: 3,
            required: true,
            fields: [
                <NICmsContentField.IChoiceField>{
                    type: NICmsContentField.EFieldType.choice,
                    key: 'type',
                    title: 'Type',
                    options: [
                        { key: 'link', title: 'Links' },
                        { key: 'description', title: 'Links with icon and description' }
                    ],
                    default: 'link',
                    required: true
                },
                <NICmsContentField.ITextField>{
                    type: NICmsContentField.EFieldType.text,
                    key: 'heading',
                    title: 'Heading',
                    required: true
                },
                <NICmsContentField.IRepeaterField>{
                    type: NICmsContentField.EFieldType.repeater,
                    key: 'items',
                    title: 'Items',
                    itemName: 'item',
                    required: true,
                    itemNameField: 'title',
                    canShow: data => data.type === 'link',
                    fields: [
                        <NICmsContentField.ITextField>{
                            type: NICmsContentField.EFieldType.text,
                            key: 'title',
                            title: 'Text',
                            required: true
                        },
                        <NICmsContentField.ITextField>{
                            type: NICmsContentField.EFieldType.text,
                            key: 'url',
                            title: 'Url'
                        }
                    ]
                },
                <NICmsContentField.IRepeaterField>{
                    type: NICmsContentField.EFieldType.repeater,
                    key: 'items',
                    title: 'Items',
                    itemName: 'item',
                    required: true,
                    itemNameField: 'title',
                    canShow: data => data.type === 'description',
                    fields: [
                        <NICmsContentField.ITextField>{
                            type: NICmsContentField.EFieldType.text,
                            key: 'title',
                            title: 'Title',
                            required: true
                        },
                        <NICmsContentField.ITextField>{
                            type: NICmsContentField.EFieldType.textarea,
                            key: 'description',
                            title: 'Description',
                            required: true
                        },
                        <NICmsContentField.IChoiceField>{
                            type: NICmsContentField.EFieldType.choice,
                            key: 'icon',
                            title: 'Icon',
                            options: iconsOptions,
                            previewMode: 'icon'
                        },
                        <NICmsContentField.ITextField>{
                            type: NICmsContentField.EFieldType.text,
                            key: 'url',
                            title: 'Url'
                        }
                    ]
                }
            ]
        },
        <NICmsContentField.IField>{
            type: NICmsContentField.EFieldType.label,
            key: 'bottomLabel',
            title: 'Bottom elements'
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'paymentMethods',
            title: 'Payment methods',
            itemName: 'Payment Method',
            itemNameField: 'name',
            fields: [
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'name',
                    title: 'Payment name'
                },
                {
                    type: NICmsContentField.EFieldType.image,
                    key: 'icon',
                    title: 'Payment icon'
                }
            ]
        },
        <NICmsContentField.IField>{
            type: NICmsContentField.EFieldType.image,
            key: 'bottomImage',
            title: 'Image'
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'bottomLinks',
            title: 'Links',
            itemName: 'Link',
            itemNameField: 'text',
            fields: [
                {
                    key: 'text',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Label',
                    required: true
                },
                {
                    key: 'url',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Link',
                    required: true
                }
            ]
        }
    ]
})
export class PageFooterComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'blockContent'
    })
    content?: NPageFooter.IData;

    readonly toggleService = inject(ToggleService);
}
